/************************************** THIRD-PARTY MODULES ***************************************/
import React from 'react';
import moment from 'moment';
import cn from 'classnames';

import {observer} from 'mobx-react';
import DatePicker from 'react-datepicker';

import {get, dateStringWithMonthTextToMoment} from 'mad-utils/lib/react';

/**************************************** PROJECT MODULES *****************************************/
import {ToasterProps, translate} from '@canimmunize/core-imports';
import {withToastNotifications} from '../../hocs/with-toast-notifications';
import {LocalDate, ZonedDateTime, extractLocalDate} from '@canimmunize/javelinscript';

/**************************************** TYPE DEFINITIONS ****************************************/
type Moment = moment.Moment;
type JVLNDate = LocalDate | ZonedDateTime;
type AnyDate = string | Moment | JVLNDate;

export interface CIDatePickerProps extends ToasterProps {
    currentDate: JVLNDate | Moment | string;
    onChange: EventHandler;
    inputClassName?: string;
    title?: string | React.ReactElement<any>;
    children?: ReactChildren;
    noDateText?: string;
    ariaLabel?: string;
    minDate?: JVLNDate | Moment | string;
    maxDate?: JVLNDate | Moment | string;
    startDate?: JVLNDate | Moment | string;
    openToDate?: JVLNDate | Moment | string;
    closeModal?: EventHandler;
    /**
     * If true, no tabIndex attached to the in-page element hosting the
     * datepicker {Default: false}
     */
    inputNotTabbable?: boolean;
    /**
     * If true, hide the 'today' button {Default: false}
     */
    hideTodayButton?: boolean;
    /**
     * ID to attach to the input field elements
     */
    id?: string;
    isClearable?: boolean;
    portalChildren?: React.ReactElement<any>;
    /**
     * IDs of elements focus shifts to before and after the datepicker, with
     * backups in case before & after IDs are not found
     */
    aroundElIds?: {
        before: string;
        beforeBackup?: string;
        after: string;
        afterBackup?: string;
    };
}

interface CIDatePickerFloatingProps extends CIDatePickerProps {
    isOpen: boolean;
    parentElId?: string;
}

/******************************************** HELPERS *********************************************/
const defID = `ci-datepicker`;

/**
 * Convert a date of any type to a moment object {Default: today}
 */
function convertToMomentDate(date: AnyDate): Moment;
function convertToMomentDate(date: AnyDate, defaultVal: AnyDate): Moment;
function convertToMomentDate(date: AnyDate, defaultVal: Moment | JVLNDate, defText: string): Moment;
function convertToMomentDate(date: AnyDate, defaultVal: string, defText: string): string | Moment;
function convertToMomentDate(date: AnyDate, defaultVal: void, defText: string): string;

// @TODO Make convertToMomentDate less confusing
/**
 * Convert a date of any type to a moment object {Default: today}
 */
function convertToMomentDate(
    date: AnyDate,
    defaultVal?: AnyDate | void,
    defText?: string
): Moment | string {
    // [1] LocalDate or ZonedDateTime date
    if (date instanceof LocalDate || date instanceof ZonedDateTime) return date.toMoment();

    // [2] Moment date
    if (moment.isMoment(date)) return date;

    // [3] String date
    if (typeof date === `string`) {
        // [3A] Return date as string if equal to click_to_schedule ID, or unspecified
        if (
            [`click_to_schedule`, `dose_trade_name_unspecified`].find(
                str => translate(str).toLowerCase() === date.toLowerCase()
            )
        ) {
            return date;
        }

        // [3B] Run specialized method for converting to moment, return if it succeeds
        const fromStrToMoment = dateStringWithMonthTextToMoment(date, {
            locale: moment.locale(),
        });
        if (fromStrToMoment) return fromStrToMoment;

        // [3C] Try converting to LocalDate & extracting moment object from it
        const asLocalDate = extractLocalDate(date);
        if (asLocalDate) return asLocalDate.toMoment();
    }

    // [4] If invalid date & no default values, return today
    //     Hidden arg may contain originally passed date arg (pre-recursion)
    if (!defaultVal && typeof defText !== `string`) {
        return moment();
    }

    // [5] Return default text if defText but not defaultVal given
    if (!defaultVal && typeof defText === `string`) return defText;

    // [6] Return attempt to convert defaultVal to moment date (falling back to defText)
    return convertToMomentDate.call(this, defaultVal as AnyDate, null, defText, date);
}

/**
 * Build Datepicker props
 * Used in both CIDatePicker & CIDatePickerFloating
 */
const commonDatePickerProps = (props: CIDatePickerProps) => {
    const {minDate, maxDate, startDate, isClearable, openToDate, ariaLabel, title} = props;

    const curDate = convertToMomentDate(props.currentDate, props.noDateText);

    let cleanStartDate = convertToMomentDate(startDate, curDate);
    if (typeof cleanStartDate === `string`) {
        cleanStartDate = typeof curDate === `string` ? moment() : curDate;
    }

    let cleanOpenToDate = convertToMomentDate(openToDate, cleanStartDate);
    if (typeof cleanOpenToDate === `string`) {
        cleanOpenToDate = typeof cleanStartDate === `string` ? moment() : cleanStartDate;
    }

    const cleanMinDate = convertToMomentDate(minDate, moment().set(`year`, 1900));
    const cleanMaxDate = convertToMomentDate(maxDate, moment().add(130, `years`));

    return {
        'aria-label': ariaLabel || title,
        onChange: props.onChange,
        autoFocus: true,
        showMonthDropdown: true,
        showYearDropdown: true,
        dropdownMode: `select` as 'select',
        openToDate: cleanOpenToDate,
        startDate: cleanStartDate,
        minDate: cleanMinDate,
        maxDate: cleanMaxDate,
        withPortal: true,
        ...(isClearable && {isClearable}),
        ...(!props.hideTodayButton && {
            todayButton: translate(`today_button`),
        }),
        ...(props.inputNotTabbable ? {tabIndex: -1} : {tabIndex: 0}),
    };
};

/********************************************* EXPORT *********************************************/
/**
 *  DatePicker detached from an input box
 *  Only render and display if [isOpen] prop is truthy
 */
export const CIDatePickerFloating = withToastNotifications(
    observer((props: CIDatePickerFloatingProps) => {
        if (props.isOpen) {
            // Props to merge into DatePicker prop
            const datePickerProps = commonDatePickerProps(props);

            const parentEl = document.getElementById(props.parentElId);

            // Hold reference to date picker DOM element
            let dpRef;

            return (
                <DatePicker
                    id={typeof props.id !== `undefined` ? props.id : `floating-datepicker`}
                    className={cn(`h-0`)}
                    customInput={<div className={cn(`h-0`)} />}
                    onClickOutside={props.closeModal}
                    onSelect={ev => parentEl && parentEl.focus()}
                    ref={_ref => {
                        // Grab reference & extract element from it, exit if none found
                        dpRef = _ref;
                        const input = dpRef && (dpRef[`input`] as HTMLInputElement);
                        if (!input) return;

                        // Inject h-0 class into grandparent, ensuring <input> stays invisible
                        const refGParClasses = get(input, `parentElement.parentElement.classList`);
                        if (refGParClasses) refGParClasses.add(`h-0`);

                        // Focus on the now-invisible input prop (to keep calendar open)
                        input.focus();

                        // Handle keypresses manually
                        input.addEventListener(`keydown`, function(ev: KeyboardEvent) {
                            // Prevent navigating out of calendar with tab
                            if (ev.key === `Tab`) {
                                ev.stopPropagation();
                                ev.preventDefault();
                                input.focus();
                            }
                            // Return to parent element if escape pressed
                            if (ev.key === `Escape`) {
                                ev.stopPropagation();
                                ev.preventDefault();
                                if (parentEl) parentEl.focus();
                                props.closeModal();
                            }
                        });
                    }}
                    {...datePickerProps}
                >
                    {props.portalChildren || null}
                </DatePicker>
            );
        } else {
            return null;
        }
    })
);

CIDatePickerFloating.displayName = `CIDatePickerFloating`;

/**
 *  Generic date picker component
 *  Input box that displays a detached date picker calendar modal when focused
 *  (either clicked or "tabbed" to)
 */
export const CIDatePicker = withToastNotifications(
    observer((props: CIDatePickerProps) => {
        const {aroundElIds} = props;

        // Build input element & its content
        const titleLocalDate = extractLocalDate(props.currentDate);
        const titleDateString =
            titleLocalDate instanceof LocalDate
                ? titleLocalDate.mediumDateString
                : props.currentDate;

        const inputElement = props.children || <div>{titleDateString}</div>;

        // Props to merge into DatePicker prop
        const datePickerProps = commonDatePickerProps(props);

        let dateRef;

        return (
            <DatePicker
                ref={_ref => {
                    dateRef = _ref;
                    const input = get(dateRef, `input`);
                    if (input) {
                        const ariaLabel = get(props, `aria-label`, props.ariaLabel || props.title);
                        input.setAttribute(
                            `id`,
                            props.id ||
                                (props.title && `${defID}-${props.title.toString().dashCase()}`) ||
                                (ariaLabel && `${defID}-${ariaLabel.toString().dashCase()}`) ||
                                defID
                        );
                    }
                }}
                onKeyDown={ev => {
                    if (aroundElIds && ev.key === `Tab`) {
                        ev.preventDefault();
                        dateRef.cancelFocusInput();

                        // Control navigation out of calendar with tab/shift-tab
                        // Grab element to navigate to & focus it
                        let el: HTMLElement;
                        if (ev.shiftKey) {
                            el = document.getElementById(aroundElIds.before);
                            if (!el) el = document.getElementById(aroundElIds.beforeBackup);
                        } else {
                            el = document.getElementById(aroundElIds.after);
                            if (!el) el = document.getElementById(aroundElIds.afterBackup);
                        }

                        el.focus();
                    }
                }}
                customInput={inputElement}
                className={cn(
                    `w-100p`,
                    `mr-n5 ml-5 p-5`,
                    `btw-0 brw-0 bbw-0pt5 blw-0 border-light-gray`,
                    `better-helvetica`,
                    `pointer`,
                    props.inputClassName
                )}
                {...datePickerProps}
            >
                {props.portalChildren || null}
            </DatePicker>
        );
    })
);

CIDatePicker.displayName = `CIDatePicker`;
